/**
 * IndexedDB LRU cache for the ExactSplatRaycaster pick index (compact resident
 * params + BVH), keyed by the immutable splat asset URL.
 *
 * Purpose: skip the SOG decompress + BVH build on every scene reopen. The editor
 * reopens the same scene dozens of times per authoring session; each reopen
 * otherwise pays a GPU readback + full-N decode + BVH build. A `Replace` mints a
 * brand-new asset URL, so a stale entry auto-misses — no explicit invalidation.
 *
 * Fidelity: stores raw Float32/typed arrays exactly as built — no quantization.
 *
 * Storage layout: TWO object stores. `entries` holds the heavy record (typed
 * arrays); `meta` holds only { key, bytes, lastUsed } scalars. Eviction reads
 * ONLY `meta` — it must never getAll() the heavy records just to sum sizes, or a
 * near-full cache would transiently materialise the entire store (hundreds of MB)
 * in the JS heap and can OOM the tab (an IndexedDB OOM aborts the tab rather than
 * throwing, so a try/catch wouldn't save us).
 *
 * Robustness: entirely best-effort. Any IndexedDB failure (private mode, disabled
 * storage, quota, Safari eviction, unavailable API) degrades to a normal rebuild;
 * nothing here ever throws into the pick-index build path.
 */

export interface PickIndexRecord {
    resident: {
        count: number;
        centers: Float32Array;
        sx: Float32Array; sy: Float32Array; sz: Float32Array;
        qx: Float32Array; qy: Float32Array; qz: Float32Array; qw: Float32Array;
        al: Float32Array;
    };
    index: {
        nodeBounds: Float32Array;
        nodeMeta: Int32Array;
        nodeCount: number;
        order: Uint32Array;
        radius: Float32Array;
    };
}

interface DataEntry { key: string; record: PickIndexRecord; }
interface MetaEntry { key: string; bytes: number; lastUsed: number; }

const DB_NAME = 'storysplat-pick-index';
const DATA_STORE = 'entries';
const META_STORE = 'meta';
const DB_VERSION = 1;
const MAX_BYTES = 400 * 1024 * 1024; // ~400 MB LRU cap (Safari-safe headroom)
// Per-entry cap: keep at least ~3 large scenes co-resident so a pair of big scenes
// can't evict each other on every reopen (100%-miss thrash that is strictly worse
// than no cache). Records above this (~2.4M splats) just rebuild each session.
const MAX_ENTRY_BYTES = Math.floor(MAX_BYTES / 3);
// Payload format version, folded into the KEY: one origin runs MIXED viewer
// versions by design (pinned scenes load older runtimes in iframes), so old and
// new record shapes must never collide on the same key. Bump on ANY shape change.
const FORMAT_VERSION = 1;
const versionedKey = (key: string): string => `v${FORMAT_VERSION}|${key}`;

function isF32(a: unknown, minLen: number): a is Float32Array {
    return a instanceof Float32Array && a.length >= minLen;
}

/**
 * Shape-validate a deserialized record BEFORE handing it to the raycaster: a
 * truncated/corrupted/foreign record installed unvalidated would make every
 * subsequent pick() throw — permanently breaking picking for the scene.
 */
function validRecord(rec: PickIndexRecord | undefined | null): rec is PickIndexRecord {
    if (!rec || !rec.resident || !rec.index) return false;
    const r = rec.resident, x = rec.index;
    const n = r.count, nc = x.nodeCount;
    if (!Number.isInteger(n) || n <= 0 || !Number.isInteger(nc) || nc <= 0) return false;
    return isF32(r.centers, n * 3)
        && isF32(r.sx, n) && isF32(r.sy, n) && isF32(r.sz, n)
        && isF32(r.qx, n) && isF32(r.qy, n) && isF32(r.qz, n) && isF32(r.qw, n)
        && isF32(r.al, n)
        && isF32(x.nodeBounds, nc * 6)
        && x.nodeMeta instanceof Int32Array && x.nodeMeta.length >= nc * 2
        && x.order instanceof Uint32Array && x.order.length >= n
        && isF32(x.radius, n);
}

function idbFactory(): IDBFactory | null {
    try {
        return typeof indexedDB !== 'undefined' ? indexedDB : null;
    } catch {
        return null; // some environments throw on access
    }
}

function openDb(): Promise<IDBDatabase | null> {
    const f = idbFactory();
    if (!f) return Promise.resolve(null);
    return new Promise((resolve) => {
        let req: IDBOpenDBRequest;
        try {
            req = f.open(DB_NAME, DB_VERSION);
        } catch {
            resolve(null);
            return;
        }
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(DATA_STORE)) db.createObjectStore(DATA_STORE, { keyPath: 'key' });
            if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE, { keyPath: 'key' });
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
    });
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function txDone(tx: IDBTransaction): Promise<void> {
    return new Promise((resolve) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
    });
}

function recordBytes(rec: PickIndexRecord): number {
    const r = rec.resident, x = rec.index;
    const arrs = [r.centers, r.sx, r.sy, r.sz, r.qx, r.qy, r.qz, r.qw, r.al, x.nodeBounds, x.nodeMeta, x.order, x.radius];
    let bytes = 0;
    for (const a of arrs) bytes += a.byteLength;
    return bytes;
}

/** Evict oldest entries until total size <= cap. Reads ONLY the small meta store. */
async function evictIfNeeded(db: IDBDatabase): Promise<void> {
    try {
        const metaTx = db.transaction(META_STORE, 'readonly');
        const metas = await reqToPromise(metaTx.objectStore(META_STORE).getAll() as IDBRequest<MetaEntry[]>);
        await txDone(metaTx);
        let total = metas.reduce((s, m) => s + (m.bytes || 0), 0);
        if (total <= MAX_BYTES) return;
        metas.sort((a, b) => a.lastUsed - b.lastUsed); // oldest first
        const victims: string[] = [];
        for (const m of metas) {
            if (total <= MAX_BYTES) break;
            victims.push(m.key);
            total -= m.bytes || 0;
        }
        if (victims.length === 0) return;
        const delTx = db.transaction([DATA_STORE, META_STORE], 'readwrite');
        const data = delTx.objectStore(DATA_STORE);
        const meta = delTx.objectStore(META_STORE);
        for (const k of victims) { data.delete(k); meta.delete(k); }
        await txDone(delTx);
    } catch {
        /* best-effort */
    }
}

/** Bump LRU recency without deserializing the heavy record. */
async function touch(key: string): Promise<void> {
    try {
        const db = await openDb();
        if (!db) return;
        const tx = db.transaction(META_STORE, 'readwrite');
        const os = tx.objectStore(META_STORE);
        const m = await reqToPromise(os.get(key) as IDBRequest<MetaEntry | undefined>);
        if (m) { m.lastUsed = Date.now(); os.put(m); }
        await txDone(tx);
        db.close();
    } catch {
        /* best-effort */
    }
}

async function doPut(vkey: string, record: PickIndexRecord): Promise<void> {
    try {
        const bytes = recordBytes(record);
        if (bytes > MAX_ENTRY_BYTES) return; // too large to cache usefully — rebuild per session
        const db = await openDb();
        if (!db) return;
        // Write record + metadata atomically in one transaction.
        const tx = db.transaction([DATA_STORE, META_STORE], 'readwrite');
        tx.objectStore(DATA_STORE).put({ key: vkey, record } as DataEntry);
        tx.objectStore(META_STORE).put({ key: vkey, bytes, lastUsed: Date.now() } as MetaEntry);
        await txDone(tx);
        await evictIfNeeded(db);
        db.close();
    } catch {
        /* best-effort */
    }
}

/** Best-effort delete of a bad entry (corrupted / stale-format). */
async function drop(vkey: string): Promise<void> {
    try {
        const db = await openDb();
        if (!db) return;
        const tx = db.transaction([DATA_STORE, META_STORE], 'readwrite');
        tx.objectStore(DATA_STORE).delete(vkey);
        tx.objectStore(META_STORE).delete(vkey);
        await txDone(tx);
        db.close();
    } catch {
        /* best-effort */
    }
}

export const pickIndexCache = {
    async get(key: string): Promise<PickIndexRecord | null> {
        try {
            const vkey = versionedKey(key);
            const db = await openDb();
            if (!db) return null;
            const tx = db.transaction(DATA_STORE, 'readonly');
            const entry = await reqToPromise(tx.objectStore(DATA_STORE).get(vkey) as IDBRequest<DataEntry | undefined>);
            await txDone(tx);
            db.close();
            if (!entry || !validRecord(entry.record)) {
                if (entry) void drop(vkey); // corrupted/foreign — purge instead of re-reading it forever
                return null;
            }
            void touch(vkey); // bump LRU recency (meta only) without blocking the read
            return entry.record;
        } catch {
            return null;
        }
    },

    async put(key: string, record: PickIndexRecord): Promise<void> {
        // The structured-clone serialization of a large record is a main-thread cost —
        // defer it off the post-build critical path when the platform allows.
        const vkey = versionedKey(key);
        const ric = (globalThis as { requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
        if (typeof ric === 'function') ric(() => { void doPut(vkey, record); }, { timeout: 5000 });
        else void doPut(vkey, record);
    },
};
